import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AiProvider } from "@/types/ai-chat";

export type ChatTabStatus = "idle" | "running" | "error" | "completed" | "stale";

export interface ChatTab {
  id: string;
  sessionId: string | null;
  provider: AiProvider;
  name: string;
  projectId: number;
  status: ChatTabStatus;
  hasUnread: boolean;
}

let tabIdCounter = 0;
const generateTabId = () => `chat-${Date.now()}-${++tabIdCounter}`;

const MAX_TABS = 12;

/** Update a single tab across all projects by ID. */
function updateTabById(
  tabs: Record<number, ChatTab[]>,
  tabId: string,
  updater: (tab: ChatTab) => ChatTab,
): Record<number, ChatTab[]> {
  const result = { ...tabs };
  for (const pid of Object.keys(result)) {
    const projectId = Number(pid);
    result[projectId] = result[projectId].map((t) =>
      t.id === tabId ? updater(t) : t,
    );
  }
  return result;
}

interface ChatTabState {
  tabs: Record<number, ChatTab[]>;
  activeTabId: Record<number, string>;

  createTab: (projectId: number, provider: AiProvider, name?: string) => string;
  closeTab: (projectId: number, tabId: string) => void;
  setActiveTab: (projectId: number, tabId: string) => void;
  renameTab: (tabId: string, title: string) => void;
  setProvider: (tabId: string, provider: AiProvider) => void;
  linkSession: (tabId: string, sessionId: string) => void;
  updateStatus: (tabId: string, status: ChatTabStatus) => void;
  markUnread: (tabId: string) => void;
  clearUnread: (tabId: string) => void;

  getTabsForProject: (projectId: number) => ChatTab[];
  getActiveTab: (projectId: number) => ChatTab | null;
  findTabBySessionId: (sessionId: string) => { tab: ChatTab; projectId: number } | null;
  purgeOrphanedTabs: (validProjectIds: Set<number>) => void;
}

export const useChatTabStore = create<ChatTabState>()(
  persist(
    (set, get) => ({
      tabs: {},
      activeTabId: {},

      createTab(projectId, provider, name) {
        const existing = get().tabs[projectId] ?? [];
        if (existing.length >= MAX_TABS) return existing[existing.length - 1].id;

        const id = generateTabId();
        const tab: ChatTab = {
          id,
          sessionId: null,
          provider,
          name: name ?? "New chat",
          projectId,
          status: "idle",
          hasUnread: false,
        };

        set((s) => ({
          tabs: { ...s.tabs, [projectId]: [...(s.tabs[projectId] ?? []), tab] },
          activeTabId: { ...s.activeTabId, [projectId]: id },
        }));

        return id;
      },

      closeTab(projectId, tabId) {
        set((s) => {
          const projectTabs = (s.tabs[projectId] ?? []).filter((t) => t.id !== tabId);
          const newActiveId =
            s.activeTabId[projectId] === tabId
              ? projectTabs[projectTabs.length - 1]?.id ?? ""
              : s.activeTabId[projectId];

          return {
            tabs: { ...s.tabs, [projectId]: projectTabs },
            activeTabId: { ...s.activeTabId, [projectId]: newActiveId },
          };
        });
      },

      setActiveTab(projectId, tabId) {
        set((s) => {
          const projectTabs = (s.tabs[projectId] ?? []).map((t) =>
            t.id === tabId ? { ...t, hasUnread: false } : t
          );
          return {
            tabs: { ...s.tabs, [projectId]: projectTabs },
            activeTabId: { ...s.activeTabId, [projectId]: tabId },
          };
        });
      },

      renameTab(tabId, title) {
        set((s) => ({ tabs: updateTabById(s.tabs, tabId, (t) => ({ ...t, name: title })) }));
      },

      setProvider(tabId, provider) {
        set((s) => ({ tabs: updateTabById(s.tabs, tabId, (t) => ({ ...t, provider })) }));
      },

      linkSession(tabId, sessionId) {
        set((s) => ({ tabs: updateTabById(s.tabs, tabId, (t) => ({ ...t, sessionId })) }));
      },

      updateStatus(tabId, status) {
        set((s) => ({ tabs: updateTabById(s.tabs, tabId, (t) => ({ ...t, status })) }));
      },

      markUnread(tabId) {
        set((s) => ({
          tabs: updateTabById(s.tabs, tabId, (t) =>
            Object.values(s.activeTabId).includes(t.id) ? t : { ...t, hasUnread: true },
          ),
        }));
      },

      clearUnread(tabId) {
        set((s) => ({ tabs: updateTabById(s.tabs, tabId, (t) => ({ ...t, hasUnread: false })) }));
      },

      getTabsForProject(projectId) {
        return get().tabs[projectId] ?? [];
      },

      getActiveTab(projectId) {
        const tabs = get().tabs[projectId] ?? [];
        const activeId = get().activeTabId[projectId];
        return tabs.find((t) => t.id === activeId) ?? null;
      },

      findTabBySessionId(sessionId) {
        const { tabs } = get();
        for (const pid of Object.keys(tabs)) {
          const projectId = Number(pid);
          const tab = tabs[projectId]?.find((t) => t.sessionId === sessionId);
          if (tab) return { tab, projectId };
        }
        return null;
      },

      purgeOrphanedTabs(validProjectIds) {
        set((s) => {
          const orphans = Object.keys(s.tabs).filter(
            (pid) => !validProjectIds.has(Number(pid)),
          );
          if (orphans.length === 0) return s;
          const tabs = { ...s.tabs };
          const activeTabId = { ...s.activeTabId };
          for (const pid of orphans) {
            delete tabs[Number(pid)];
            delete activeTabId[Number(pid)];
          }
          return { tabs, activeTabId };
        });
      },
    }),
    {
      name: "colony-chat-tabs",
      version: 1,
      partialize: (state) => ({
        tabs: state.tabs,
        activeTabId: state.activeTabId,
      }),
    }
  )
);
